import { useEffect } from "react";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_URL,
  formatTitle,
} from "./siteMeta";

// ページ別の title / description / canonical / OGP を <head> に反映する。
//
// public/index.html に既にある静的タグは「作らずに書き換える」方針で扱う。
// これにより同種タグが二重に並ばず、クローラーが先頭の古い値を読む事故を防ぐ。
//
// 【重要な制約】
// CRAのSPAなので、ここでの書き換えはJS実行後にしか反映されない。
// Googlebotはレンダリングするため拾えるが、X(Twitter)・Facebook・Slack等の
// OGPクローラーはJSを実行しないため、シェア時のカードは index.html の
// 既定値のままになる。ページ別OGPを完全に効かせるにはプリレンダリング
// （react-snap 等）か、該当ページのサーバーサイド化が別途必要。

/** name/property属性でmetaタグを引き、無ければ作って content を設定する。 */
function setMetaTag(attr, key, content) {
  if (!content) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/** rel="canonical" を設定する。 */
function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** 絶対URLに整える（OGPは相対パスを解釈しないクローラーがあるため）。 */
function toAbsolute(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

/**
 * @param {object} opts
 * @param {string} [opts.title]       ページ名（「| LEAPS」は自動付与）
 * @param {string} [opts.description] メタディスクリプション
 * @param {string} [opts.image]       OGP画像URL（相対可）
 * @param {string} [opts.path]        canonical用のパス。省略時は現在のURL
 * @param {boolean} [opts.noindex]    trueで noindex, nofollow
 * @param {string} [opts.type]        og:type（既定 website、記事は article）
 */
export function useSeo({
  title,
  description,
  image,
  path,
  noindex = false,
  type = "website",
} = {}) {
  useEffect(() => {
    const fullTitle = formatTitle(title);
    const desc = description || DEFAULT_DESCRIPTION;
    const ogImage = toAbsolute(image) || DEFAULT_OG_IMAGE;
    const canonical = path
      ? `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`
      : `${SITE_URL}${window.location.pathname}`;

    document.title = fullTitle;

    setMetaTag("name", "description", desc);
    setCanonical(canonical);

    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:title", fullTitle);
    setMetaTag("property", "og:description", desc);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:url", canonical);
    setMetaTag("property", "og:site_name", DEFAULT_TITLE);

    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", fullTitle);
    setMetaTag("name", "twitter:description", desc);
    setMetaTag("name", "twitter:image", ogImage);

    // STG（stg.leaps-inc.com）は deploy-stg.yml が index.html に noindex を
    // 注入している。ここで無条件に "index, follow" を書くとそれを打ち消して
    // STGがインデックスされてしまうため、本番ホスト以外は常に noindex にする。
    //
    // プリレンダー（scripts/prerender.mjs）は 127.0.0.1 で開くが、書き出した HTML は本番に置く。
    // そこで noindex になると本番の全ページが検索から消えるため、本番ホストとして扱う。
    // STG では deploy-stg.yml が書き出した全 HTML の robots を noindex に置き換える。
    const host = window.location.hostname;
    const isPrerender = /LEAPS-Prerender/.test(navigator.userAgent);
    const isProdHost =
      isPrerender || host === "www.leaps-inc.com" || host === "leaps-inc.com";
    setMetaTag(
      "name",
      "robots",
      noindex || !isProdHost ? "noindex, nofollow" : "index, follow",
    );
  }, [title, description, image, path, noindex, type]);
}
